import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useRaelaCoPilot, type RaelaSuggestion } from "@/lib/messaging/useRaelaCoPilot";

interface Props {
  conversationId: string | null;
  draft: string;
  recentMessages: { role: "me" | "them"; text: string }[];
  app?: "fryqnc" | "pulsynq" | "mindlynq";
  onPick: (text: string) => void;
}

const TONE_COLORS: Record<string, string> = {
  warm: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  playful: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  direct: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",
};

const RaelaCoPilotButton = ({
  conversationId,
  draft,
  recentMessages,
  app,
  onPick,
}: Props) => {
  const { suggestions, loading, generate, markUsed, reset } = useRaelaCoPilot();
  const [open, setOpen] = useState(false);

  const handleOpen = async (next: boolean) => {
    setOpen(next);
    if (next && suggestions.length === 0 && !loading) {
      await generate({ conversationId, draft, recentMessages, app });
    }
    if (!next) {
      reset();
    }
  };

  const pick = async (s: RaelaSuggestion, index: number) => {
    onPick(s.text);
    void markUsed(index);
    setOpen(false);
    reset();
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs"
          aria-label="RAELA Co-pilot"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Co-pilot
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              RAELA suggests
            </p>
            <button
              type="button"
              onClick={() => generate({ conversationId, draft, recentMessages, app })}
              className="text-xs text-muted-foreground hover:text-foreground"
              disabled={loading}
            >
              Refresh
            </button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-xs">Thinking…</span>
            </div>
          ) : suggestions.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No ideas yet.</p>
          ) : (
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(s, i)}
                  className="w-full text-left rounded-lg border border-border bg-card hover:bg-accent transition-colors p-3"
                >
                  <span
                    className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border mb-1 ${
                      TONE_COLORS[s.tone] ?? "border-border text-muted-foreground"
                    }`}
                  >
                    {s.tone}
                  </span>
                  <p className="text-sm leading-snug">{s.text}</p>
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground pt-1">
            Suggestions are private. You always send.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RaelaCoPilotButton;
